import { Template } from 'cruzo';
import { delay } from 'cruzo/utils';

export function scrollExerciseStageToTop(behavior: ScrollBehavior = "auto") {
  const stageBody = document.querySelector(".xq-exercise-stage-body");
  if (stageBody instanceof HTMLElement) {
    stageBody.scrollTo({ top: 0, behavior });
    return;
  }
  window.scrollTo({ top: 0, behavior });
}

export function stylesHashToString(styles: { [key: string]: any }) {
  const keys = Object.keys(styles);
  let result = '';
  let i = 0;

  while (i < keys.length) {
    let property = keys[i];
    let value = styles[keys[i]];
    result += `${property}: ${value};`;
    i++;
  }

  return result;
}

export function getParamsForAnim(value: any, params: any) {
  let { speed, type } = params;
  const cls = [];

  speed = speed || 0.5;
  type = type || 'default';

  if (value) {
    cls.push(`container_co_${type}--opened`);
  } else {
    cls.push(`container_co_${type}--closed`);
  }

  let speedCss = '';

  if (speed === 0.5) {
    speedCss = '0_5';
  }

  if (speed === 1.5) {
    speedCss = '1_5';
  }

  cls.push(`container_co_${speedCss}`);
  cls.push(`container_co_${type}`);

  return cls.join(' ');
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function whenAnimationDone(animation: Animation): Promise<void> {
  return animation.finished.then((): void => undefined).catch((): void => undefined);
}

export function waitAnimationFrames(count: number): Promise<void> {
  return new Promise((resolve) => {
    let left = count;
    const tick = () => {
      left -= 1;
      if (left <= 0) {
        resolve();
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

export function runStageVeilCover(
  veil: HTMLElement | null | undefined,
  body: HTMLElement | null | undefined,
): Promise<void> {
  veil?.getAnimations().forEach((animation) => animation.cancel());
  body?.getAnimations().forEach((animation) => animation.cancel());

  if (veil) {
    veil.style.opacity = "1";
    veil.style.pointerEvents = "auto";
  }

  if (body) {
    body.style.visibility = "hidden";
    body.style.opacity = "";
    body.style.transform = "";
  }

  return Promise.resolve();
}

export function runStageVeilUncover(
  veil: HTMLElement | null | undefined,
  body: HTMLElement | null | undefined,
): Promise<void> {
  if (body) {
    body.style.visibility = "";
    body.style.opacity = "";
    body.style.transform = "";
  }

  if (veil) {
    veil.style.opacity = "0";
    veil.style.pointerEvents = "";
  }

  return Promise.resolve();
}

export function openCloseAnim(value: any, params = {}) {
  const cls = [];

  const paramsClass = getParamsForAnim(value, params);

  if (paramsClass) {
    cls.push(paramsClass);
  }

  return cls.join(' ');
}

export function initAnimationElements(animationElements: any, template: Template) {
  const keys = Object.keys(animationElements) as any[];

  let i = 0;

  while (i < keys.length) {
    (async (index) => {
      const key = keys[index];
      const item = animationElements[key];

      await delay(item.delay());
      animationElements[key] = false;
      template.detectChanges();
    })(i);

    i++;
  }
}

export function getRandomArrayIndexes(array: any[], maxLength: number) {
  const indexes = array.map((v, index) => index).sort(() => Math.random() - 0.5);

  if (indexes.length > maxLength) {
    indexes.length = maxLength;
  }

  return indexes;
}

export function daysLeft(date: number) {
  return Math.max(
    0,
    Math.floor((new Date(date).getTime() - new Date().getTime())/1000/60/60/24)
  );
}

export function plural(number: number, translate: any) {
    const {one, two, five} = translate;

    let n = Math.abs(number);
    n %= 100;
    if (n.toString().includes('.')) {
      return two;
    }
    if (n >= 5 && n <= 20) {
      return five;
    }
    n %= 10;
    if (n === 1) {
      return one;
    }
    if (n >= 2 && n <= 4) {
      return two;
    }
    return five;
}

export function humanLikeTime(ms: number, trs: any) {
    const delta = Math.round((+new Date - ms) / 1000);

    const minute = 60;
    const hour = minute * 60;
    const day = hour * 24;
    const week = day * 7;

    if (delta < 30) {
      return 'только что';
    } else if (delta < minute) {
      return delta + ` ${plural(delta, trs.ru.seconds)} назад`;
    } else if (delta < 2 * minute) {
      return `минуту назад`
    } else if (delta < hour) {
      const minutes = Math.floor(delta / minute);

      return minutes + ` ${plural(minutes, trs.ru.minutes)} назад`;
    } else if (Math.floor(delta / hour) == 1) {
      return `1 час назад`
    } else if (delta < day) {
      const hours = Math.floor(delta / hour);

      return hours + ` ${plural(hours, trs.ru.hours)} назад`;
    } else if (delta < day * 2) {
      return `вчера`;
    } else {
      return new Date(ms).toLocaleString();
    }
}

export const capitalizeFirst = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export function toBufferSource(bytes: Uint8Array): BufferSource {
  return Uint8Array.from(bytes);
}